import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { Subject, of } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import { FamilyMemberFormComponent } from '../../../../shared/components/family-member-form/family-member-form.component';
import type { FamilyFormOption } from '../../../../shared/types/family-member-form.types';
import { AppConfigService } from '../../../../services/app-config.service';
import { FamilyPlanSettingsService } from '../../../../services/family-plan-settings.service';
import { FAMILY_PLAN_FEATURE, isFeatureOn } from '../../../../shared/utils/app-feature.util';
import { formatFamilyValue } from '../../../../shared/utils/family-plan-settings.util';
import type { FamilyPlanSettingsData } from '../../../../shared/types/family-plan-settings.types';
import type { FamilyMemberDraft } from '../../../../shared/types/family-sale.types';
import type { OrderRequestExpandedPackage } from '../../models/notification-order-request.models';
import {
  FamilyMemberErrors,
  canAddMember,
  canOfferFamilySale,
  familyPackageLabel,
  familyPackageListPrice,
  familyTakenPlates,
  formatFamilyMoney,
  hasMemberErrors,
  memberFullName,
  normalizePlate,
  previewMemberPrice,
  remainingMemberSlots,
  validateFamilyMember,
} from '../../utils/notification-family-sale.util';

interface FamilyMemberForm {
  firstName: string;
  lastName: string;
  phone: string;
  plate: string;
  state: string;
  packageId: string;
  discountValue: number | null;
}

/**
 * "Sell as Family Plan" section of the Order Request modal (CWS-1580): toggle, primary summary,
 * member cards and the add-member form. Loads its own flag and settings; the host only passes the
 * primary booking data and reads the members through {@link membersChange}.
 */
@Component({
  selector: 'app-order-request-family-sale-block',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSelectModule, FamilyMemberFormComponent],
  templateUrl: './order-request-family-sale-block.component.html',
  styleUrls: ['./order-request-family-sale-block.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class OrderRequestFamilySaleBlockComponent implements OnInit, OnChanges, OnDestroy {
  private readonly appConfig = inject(AppConfigService);
  private readonly settingsService = inject(FamilyPlanSettingsService);
  private readonly destroy$ = new Subject<void>();
  private readonly settingsRequest$ = new Subject<string>();

  @Input() locationId = '';
  /** The selected main package is a family-eligible monthly membership. */
  @Input() primaryEligible = false;
  @Input() primaryPackageName = '';
  @Input() primaryListPrice: number | null = null;
  @Input() primaryPlate = '';
  @Input() primaryState = '';
  @Input() primaryCustomerName = '';
  @Input() primaryPhone = '';
  @Input() primaryVehicleName = '';
  @Input() eligiblePackages: OrderRequestExpandedPackage[] = [];
  @Input() states: readonly { code: string; name: string }[] = [];
  @Input() disabled = false;

  @Output() membersChange = new EventEmitter<FamilyMemberDraft[]>();
  /** True while the toggle is on and the section is shown. */
  @Output() activeChange = new EventEmitter<boolean>();

  flagOn = false;
  settingsData: FamilyPlanSettingsData | null = null;
  enabled = false;
  members: FamilyMemberDraft[] = [];
  formOpen = false;
  /** The member whose details the form is editing; null when it adds a new one. */
  editingId: string | null = null;
  form: FamilyMemberForm = this.emptyForm();
  errors: FamilyMemberErrors = {};

  private optionsPackages: OrderRequestExpandedPackage[] | null = null;
  private optionsSettings: FamilyPlanSettingsData | null = null;
  private packageOptionsCache: FamilyFormOption[] = [];
  private discountOptionsCache: FamilyFormOption<number>[] = [];

  ngOnInit(): void {
    this.appConfig
      .getAppConfig()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: config => (this.flagOn = isFeatureOn(config, FAMILY_PLAN_FEATURE)),
        error: () => (this.flagOn = false),
      });
    this.settingsRequest$
      .pipe(
        switchMap(id =>
          id ? this.settingsService.getSettings(id).pipe(catchError(() => of(null))) : of(null)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(res => {
        this.settingsData = res?.data ?? null;
        this.reset();
      });
    this.settingsRequest$.next(this.locationId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['locationId'] && !changes['locationId'].firstChange) {
      // The old location's settings and members must not be submittable while the new ones load.
      this.settingsData = null;
      this.reset();
      this.settingsRequest$.next(this.locationId);
    }
    if (changes['primaryEligible'] && !this.primaryEligible) {
      this.reset();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get visible(): boolean {
    return canOfferFamilySale(this.flagOn, this.settingsData) && this.primaryEligible;
  }

  get presets(): number[] {
    return this.settingsData?.settings?.presets ?? [];
  }

  get maxVehicles(): number {
    return this.settingsData?.settings?.max_vehicles ?? 0;
  }

  get canAdd(): boolean {
    const settings = this.settingsData?.settings;
    return !!settings && canAddMember(settings, this.members.length);
  }

  get atLimit(): boolean {
    const settings = this.settingsData?.settings;
    return !!settings && remainingMemberSlots(settings, this.members.length) === 0;
  }

  get takenPlates(): string[] {
    return familyTakenPlates(this.primaryPlate, this.members, this.editingId);
  }

  /** The form's package and discount lists, rebuilt only when their sources change. */
  get packageOptions(): FamilyFormOption[] {
    if (this.optionsPackages !== this.eligiblePackages) {
      this.optionsPackages = this.eligiblePackages;
      this.packageOptionsCache = this.eligiblePackages.map(item => ({
        value: item.package.id,
        label: this.packageLabel(item),
      }));
    }
    return this.packageOptionsCache;
  }

  get discountOptions(): FamilyFormOption<number>[] {
    if (this.optionsSettings !== this.settingsData) {
      this.optionsSettings = this.settingsData;
      this.discountOptionsCache = this.presets.map(value => ({
        value,
        label: this.discountLabel(value),
      }));
    }
    return this.discountOptionsCache;
  }

  get formPrice(): { discount: number; recurringPrice: number; list: number } | null {
    const item = this.packageById(this.form.packageId);
    if (!item || this.form.discountValue == null) {
      return null;
    }
    const list = familyPackageListPrice(item);
    return { list, ...this.preview(list, this.form.discountValue) };
  }

  reset(): void {
    this.enabled = false;
    this.members = [];
    this.formOpen = false;
    this.editingId = null;
    this.form = this.emptyForm();
    this.errors = {};
    this.emit();
  }

  onToggleChange(event: Event): void {
    this.setEnabled((event.target as HTMLInputElement).checked);
  }

  setEnabled(checked: boolean): void {
    this.enabled = checked;
    if (checked && this.members.length === 0) {
      this.openForm();
    } else if (!checked) {
      this.formOpen = false;
      this.editingId = null;
    }
    this.emit();
  }

  openForm(): void {
    this.editingId = null;
    this.form = this.emptyForm();
    this.errors = {};
    this.formOpen = true;
  }

  editMember(localId: string): void {
    const member = this.members.find(m => m.localId === localId);
    if (!member) {
      return;
    }
    this.editingId = localId;
    this.form = {
      firstName: member.firstName,
      lastName: member.lastName,
      phone: member.phone,
      plate: member.plate,
      state: member.state,
      packageId: member.packageId,
      discountValue: member.discountValue,
    };
    this.errors = {};
    this.formOpen = true;
  }

  cancelForm(): void {
    this.formOpen = false;
    this.editingId = null;
    this.errors = {};
  }

  saveMember(): void {
    const settings = this.settingsData?.settings;
    if (!settings) {
      return;
    }
    this.errors = validateFamilyMember(this.form, this.takenPlates, this.presets);
    if (hasMemberErrors(this.errors)) {
      return;
    }
    const saved: FamilyMemberDraft = {
      localId: this.editingId ?? crypto.randomUUID(),
      firstName: this.form.firstName.trim(),
      lastName: this.form.lastName.trim(),
      phone: this.form.phone,
      plate: normalizePlate(this.form.plate),
      state: this.form.state,
      packageId: this.form.packageId,
      discountType: settings.discount_type,
      discountValue: this.form.discountValue as number,
    };
    this.members = this.editingId
      ? this.members.map(m => (m.localId === this.editingId ? saved : m))
      : [...this.members, saved];
    this.editingId = null;
    this.formOpen = false;
    this.emit();
  }

  removeMember(localId: string): void {
    this.members = this.members.filter(m => m.localId !== localId);
    if (this.editingId === localId) {
      this.cancelForm();
    }
    this.emit();
  }

  onMemberDiscountChange(localId: string, value: number): void {
    this.members = this.members.map(m =>
      m.localId === localId ? { ...m, discountValue: value } : m
    );
    if (this.editingId === localId) {
      // The open form holds the old value and would overwrite this one on Save.
      this.form = { ...this.form, discountValue: value };
    }
    this.emit();
  }

  packageById(packageId: string): OrderRequestExpandedPackage | undefined {
    return this.eligiblePackages.find(item => item.package.id === packageId);
  }

  packageLabel(item: OrderRequestExpandedPackage): string {
    return familyPackageLabel(item);
  }

  memberFullName(member: FamilyMemberDraft): string {
    return memberFullName(member);
  }

  memberPackageName(member: FamilyMemberDraft): string {
    return this.packageById(member.packageId)?.displayName ?? '';
  }

  memberListPrice(member: FamilyMemberDraft): number {
    return familyPackageListPrice(this.packageById(member.packageId));
  }

  memberRecurringPrice(member: FamilyMemberDraft): number {
    return this.preview(this.memberListPrice(member), member.discountValue).recurringPrice;
  }

  discountLabel(value: number): string {
    return formatFamilyValue(this.settingsData?.settings?.discount_type ?? null, value);
  }

  money(amount: number | null): string {
    return formatFamilyMoney(amount ?? 0);
  }

  trackById(_index: number, member: FamilyMemberDraft): string {
    return member.localId;
  }

  private preview(list: number, value: number): { discount: number; recurringPrice: number } {
    const type = this.settingsData?.settings?.discount_type ?? 'percentage';
    return previewMemberPrice(list, type, value);
  }

  private emptyForm(): FamilyMemberForm {
    return {
      firstName: '',
      lastName: '',
      phone: '',
      plate: '',
      state: this.primaryState,
      packageId: '',
      discountValue: this.settingsData?.settings?.default_discount_value ?? null,
    };
  }

  private emit(): void {
    this.membersChange.emit(this.members);
    this.activeChange.emit(this.enabled && this.visible);
  }
}
